import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Mail, MessageCircle, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BRAND } from "@/lib/site";
import { Reveal, Stagger, StaggerItem, MaskLines } from "@/components/Reveal";
import { Stadtmusikanten, BremenSkyline } from "@/components/graphics/Marks";
import Snowfall from "@/components/Snowfall";

export const Label = ({ children, className = "", tone = "blue" }) => (
  <span
    className={`font-mono-label ${tone === "orange" ? "text-[#FF6B00]" : tone === "white" ? "text-white/70" : "text-[#0757B8]"} ${className}`}
  >
    {children}
  </span>
);

export const Breadcrumbs = ({ items }) => (
  <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-[#66788A]">
    {items.map((it, i) => (
      <span key={it.to} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-[#DCE5EF]" />}
        {i === items.length - 1 ? (
          <span className="font-medium text-[#10253A]">{it.label}</span>
        ) : (
          <Link to={it.to} className="transition-colors hover:text-[#0757B8]">
            {it.label}
          </Link>
        )}
      </span>
    ))}
  </nav>
);

export const PageHero = ({ label, titleLines, intro, image, breadcrumbs, ctaPrimary, ctaSecondary }) => (
  <section data-testid="page-hero" className="relative overflow-hidden bg-[#062B52] pb-16 pt-[124px] text-white lg:pb-24 lg:pt-[182px]">
    {image && (
      <>
        <img
          src={image}
          alt=""
          aria-hidden="true"
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#062B52] via-[#062B52]/92 to-[#062B52]/55" />
      </>
    )}
    <Snowfall count={18} />
    <BremenSkyline className="pointer-events-none absolute inset-x-0 bottom-0 h-24 w-full opacity-[0.14]" color="#FFFFFF" />
    <Stadtmusikanten className="pointer-events-none absolute -right-4 bottom-0 h-[300px] opacity-[0.1]" color="#FFFFFF" />

    <div className="shell relative z-10">
      {breadcrumbs && (
        <div className="mb-8 flex flex-wrap items-center gap-1.5 text-[12.5px] text-white/55">
          {breadcrumbs.map((it, i) => (
            <span key={it.to} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-white/30" />}
              {i === breadcrumbs.length - 1 ? (
                <span className="font-medium text-white">{it.label}</span>
              ) : (
                <Link to={it.to} className="hover:text-[#FF6B00]">
                  {it.label}
                </Link>
              )}
            </span>
          ))}
        </div>
      )}
      <Label tone="orange">{label}</Label>
      <h1
        className="mt-5 max-w-4xl text-[32px] font-extrabold leading-[1.08] sm:text-[44px] lg:text-[60px]"
        style={{ color: "#fff" }}
      >
        <MaskLines lines={titleLines} delay={0.05} />
      </h1>
      {intro && (
        <Reveal delay={0.25}>
          <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-white/75 sm:text-base">{intro}</p>
        </Reveal>
      )}
      <Reveal delay={0.35}>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/kontakt" data-testid="pagehero-cta" className="btn btn-orange h-13 px-6 py-3.5 text-sm">
            {ctaPrimary || "Winterdienst anfragen"} <ArrowUpRight className="h-4 w-4" />
          </Link>
          {ctaSecondary ? (
            <Link
              to="/kontakt"
              data-testid="pagehero-cta-secondary"
              className="btn h-13 border border-white/25 px-6 py-3.5 text-sm text-white hover:border-white/60"
            >
              {ctaSecondary} <ArrowUpRight className="h-4 w-4" />
            </Link>
          ) : null}
          <a href={BRAND.phoneHref} data-testid="pagehero-phone" className="btn h-13 border border-white/25 px-6 py-3.5 text-sm text-white hover:border-white/60">
            <Phone className="h-4 w-4" /> Jetzt anrufen
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const SectionHead = ({ label, title, text, align = "left", light = false }) => (
  <Reveal className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
    {label && <Label tone={light ? "orange" : "blue"}>{label}</Label>}
    <h2
      className={`mt-4 text-[26px] font-bold leading-[1.15] sm:text-[32px] lg:text-[40px] ${light ? "text-white" : ""}`}
      style={light ? { color: "#fff" } : undefined}
    >
      {title}
    </h2>
    {text && (
      <p className={`mt-5 text-[15px] leading-relaxed sm:text-base ${light ? "text-white/70" : "text-[#66788A]"}`}>
        {text}
      </p>
    )}
  </Reveal>
);

export const CheckRow = ({ children, light = false }) => (
  <li className="flex items-start gap-3">
    <span
      className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${light ? "bg-white" : "bg-[#EEF6FF]"}`}
    >
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none">
        <path d="M5 12.5l4.5 4.5L19 7.5" stroke={light ? "#FF6B00" : "#0757B8"} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className={`text-[14.5px] leading-relaxed ${light ? "text-white/85" : "text-[#10253A]"}`}>{children}</span>
  </li>
);

export const FaqBlock = ({ items, title = "Häufige Fragen", label = "/ FAQ", moreTo }) => (
  <section data-testid="faq-block" className="bg-[#F5F8FC] py-20 lg:py-28">
    <div className="shell grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <SectionHead label={label} title={title} />
        {moreTo && (
          <Reveal delay={0.06}>
            <Link
              to={moreTo}
              data-testid="faq-more-link"
              className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#0757B8] transition-colors hover:text-[#FF6B00]"
            >
              Alle Fragen ansehen <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        )}
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl border border-[#DCE5EF] bg-white p-6">
            <p className="text-sm text-[#66788A]">Ihre Frage ist nicht dabei?</p>
            <a href={BRAND.phoneHref} className="mt-2 block text-lg font-bold text-[#0757B8]" data-testid="faq-phone">
              {BRAND.phone}
            </a>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-wa mt-4 h-11 w-full text-sm"
              data-testid="faq-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> Per WhatsApp fragen
            </a>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.12}>
        <Accordion type="single" collapsible className="divide-y divide-[#DCE5EF] border-y border-[#DCE5EF]">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-0">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="py-5 text-left text-[15.5px] font-semibold text-[#10253A] hover:text-[#0757B8] hover:no-underline sm:text-base"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-[14.5px] leading-relaxed text-[#66788A]">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const CtaBand = ({
  title = "Winterdienst in Bremen anfragen",
  text = "Kurz anrufen oder eine WhatsApp senden – wir klären Fläche, Umfang und Ablauf und machen Ihnen ein Angebot.",
}) => (
  <section data-testid="cta-band" className="relative overflow-hidden bg-[#0757B8] py-20 text-white lg:py-24">
    <Snowfall count={16} />
    <Stadtmusikanten className="pointer-events-none absolute -left-6 -bottom-10 h-[340px] opacity-[0.08]" color="#FFFFFF" />
    <div className="shell relative z-10 grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
      <div>
        <Label tone="white">/ Kontakt</Label>
        <h2 className="mt-4 text-[28px] font-bold leading-[1.15] sm:text-[34px] lg:text-[42px]" style={{ color: "#fff" }}>
          {title}
        </h2>
        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/80 sm:text-base">{text}</p>
      </div>
      <div className="grid gap-3">
        <a href={BRAND.phoneHref} data-testid="ctaband-phone" className="group flex items-center justify-between rounded-2xl bg-white/10 px-5 py-4 transition-colors hover:bg-white/18">
          <span className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-[#FF6B00]" />
            <span className="text-[15px] font-semibold">{BRAND.phone}</span>
          </span>
          <ArrowUpRight className="h-4 w-4 opacity-60 transition-transform group-hover:translate-x-0.5" />
        </a>
        <a
          href={BRAND.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ctaband-whatsapp"
          className="flex items-center justify-between rounded-2xl bg-[#25D366] px-5 py-4 transition-transform hover:-translate-y-0.5"
        >
          <span className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5" />
            <span className="text-[15px] font-semibold">Per WhatsApp kontaktieren</span>
          </span>
          <ArrowUpRight className="h-4 w-4 opacity-80" />
        </a>
        <a href={BRAND.emailHref} data-testid="ctaband-email" className="flex items-center justify-between rounded-2xl bg-white/10 px-5 py-4 transition-colors hover:bg-white/18">
          <span className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-[#FF6B00]" />
            <span className="break-all text-[14px] font-semibold">{BRAND.email}</span>
          </span>
        </a>
        <Link to="/kontakt" data-testid="ctaband-cta" className="btn btn-orange h-13 py-3.5 text-sm">
          Winterdienst anfragen <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

export const RelatedLinks = ({ items, title = "Passend dazu" }) => (
  <section data-testid="related-links" className="border-t border-[#DCE5EF] bg-white py-16 lg:py-20">
    <div className="shell">
      <SectionHead label="/ Weiterlesen" title={title} />
      <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => (
          <StaggerItem key={r.to}>
            <Link
              to={r.to}
              data-testid={`related-${r.to.replace(/\//g, "")}`}
              className="group flex h-full items-start justify-between gap-4 rounded-2xl border border-[#DCE5EF] bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#0757B8] hover:shadow-[0_24px_60px_-40px_rgba(6,43,82,0.5)]"
            >
              <span>
                <span className="block text-[15.5px] font-semibold text-[#10253A] group-hover:text-[#0757B8]">
                  {r.title}
                </span>
                {r.desc && <span className="mt-2 block text-[13.5px] leading-relaxed text-[#66788A]">{r.desc}</span>}
              </span>
              <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B00] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const Marquee = ({ items }) => {
  const row = [...items, ...items];
  return (
    <div className="marquee-wrap relative overflow-hidden border-y border-[#DCE5EF] bg-white py-5">
      <div className="marquee-track items-center gap-10">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-10">
            <span className="font-mono-label whitespace-nowrap text-[#66788A]">{t}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF6B00]" />
          </span>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent" />
    </div>
  );
};
